import { useGetStationsOnLangsteRoute } from "@/entities/langste-route";
import { StationsAutocomplete } from "@/features/infra";
import { TreinseriesAutocomplete } from "@/features/infra/components/treinseries-autocomplete.tsx";
import {
    Button,
    FormControl,
    FormField,
    FormFieldInput,
    FormItem,
    FormMessage,
    SingleToggleGroup,
    ToggleGroupOption,
} from "@/shared/components";
import { ReactNode } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

type RequiredFormFieldProps<T extends FieldValues> = {
    form: UseFormReturn<T>;
    name: FieldPath<T>;
};

export function SerieInput<T extends FieldValues>({ form, name }: Readonly<RequiredFormFieldProps<T>>) {
    return (
        <div className="flex max-w-34 items-center gap-4">
            <TreinseriesAutocomplete
                className="text-content-default placeholder:text-content-placeholder"
                form={form}
                name={name}
                placeholder="Serie"
                showSearchIcon={false}
            />
        </div>
    );
}

type StationSelectProps<T extends FieldValues> = RequiredFormFieldProps<T> & {
    placeholder: string;
    forTreinserie: string | undefined;
};

export function StationSelect<T extends FieldValues>({ form, name, placeholder, forTreinserie }: Readonly<StationSelectProps<T>>) {
    const { data: treinserieMetTrajectStations } = useGetStationsOnLangsteRoute(forTreinserie);

    return (
        <div className={"w-max-[220px] text-content-default flex w-full min-w-0 items-center gap-4"}>
            <StationsAutocomplete
                data-testid={`stations-autocomplete-${name}`}
                form={form}
                name={name}
                showSearchIcon={false}
                placeholder={placeholder}
                label={null}
                preferredStations={treinserieMetTrajectStations?.stations}
            />
        </div>
    );
}

export function OpmerkingField<T extends FieldValues>({ form, name }: Readonly<RequiredFormFieldProps<T>>) {
    return <FormFieldInput form={form} name={name} placeholder="Opmerking (optioneel)" className="text-content-default" />;
}

type FormButtonsProps = { onCancel: () => void; isSubmitting: boolean; submitLabel: ReactNode };
export function FormButtons({ onCancel, isSubmitting, submitLabel }: Readonly<FormButtonsProps>) {
    return (
        <div className="flex justify-end gap-2">
            <Button type="button" onClick={onCancel} variant="tertiary">
                Annuleren
            </Button>
            <Button type="submit" disabled={isSubmitting} variant="secondary">
                {submitLabel}
            </Button>
        </div>
    );
}

type Richting = "ViceVersa" | "Heen";

const richtingOptions: ToggleGroupOption<Richting>[] = [
    { icon: "arrow-right-left", value: "ViceVersa" },
    { icon: "arrow-right", value: "Heen" },
];

export function RichtingSwitch<T extends FieldValues>({ form, name }: Readonly<RequiredFormFieldProps<T>>) {
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <FormControl>
                        <SingleToggleGroup options={richtingOptions} {...field} onValueChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

type MaatregelType = "Omleiding" | "ExtraStop" | "StopVerwijderen";

const maatregelTypeOptions: ToggleGroupOption<MaatregelType>[] = [
    { value: "Omleiding", label: "Omleiding" },
    { value: "ExtraStop", label: "Extra Stop" },
    { value: "StopVerwijderen", label: "Stopt niet" },
];

export function MaatregelTypeSwitch<T extends FieldValues>({ form, name }: Readonly<RequiredFormFieldProps<T>>) {
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem className="w-full">
                    <FormControl>
                        <SingleToggleGroup options={maatregelTypeOptions} {...field} onValueChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
